/**
 * "Next case" and "Previous": the order of the queue the case was opened from.
 *
 * When sales open a row, the queue writes a snapshot into this tab's `sessionStorage`
 * (`i2q.queue.order`): the ids of the rows on screen in their order (at most 200), the
 * queue's search string and where the page starts. The case page walks that list. A case
 * closed from the case page ("Mark done and next") is remembered in the snapshot and
 * skipped from then on, although it is still in the list. When the list runs out, the
 * case page reads the next queue page with the same filters and appends it.
 *
 * Storage can be unavailable (private mode, blocked): every access is guarded and the
 * case page then simply shows no Next / Previous.
 */

export const QUEUE_ORDER_KEY = "i2q.queue.order";
/** The most ids one snapshot keeps. */
export const MAX_ORDER = 200;

export interface QueueOrder {
  /** Case ids in queue order. */
  ids: string[];
  /** The queue's search string (without "?"), to read the next page with the same filters. */
  search: string;
  /** Rows of the queue before `ids[0]` (the page start). */
  offset: number;
  /** Rows under the queue's filters, all pages. */
  total: number;
  /** Cases closed from the case page during this walk: skipped from now on. */
  closed: string[];
}

function isOrder(raw: unknown): raw is QueueOrder {
  const value = raw as Partial<QueueOrder> | null;
  return (
    value != null &&
    Array.isArray(value.ids) &&
    value.ids.every((id) => typeof id === "string") &&
    typeof value.search === "string"
  );
}

/** A snapshot from an untrusted value; null when it is not one. */
export function toQueueOrder(raw: unknown): QueueOrder | null {
  if (!isOrder(raw)) {
    return null;
  }
  const ids = [...new Set(raw.ids.filter((id) => id.trim() !== ""))].slice(0, MAX_ORDER);
  return {
    ids,
    search: raw.search,
    offset: typeof raw.offset === "number" && raw.offset >= 0 ? Math.floor(raw.offset) : 0,
    total: typeof raw.total === "number" && raw.total >= 0 ? Math.floor(raw.total) : ids.length,
    closed: Array.isArray(raw.closed)
      ? raw.closed.filter((id): id is string => typeof id === "string")
      : [],
  };
}

/** Write the snapshot of the queue on screen. Fails silently without storage. */
export function saveQueueOrder(order: Omit<QueueOrder, "closed"> & { closed?: string[] }): void {
  const clean = toQueueOrder({ closed: [], ...order });
  if (!clean) {
    return;
  }
  try {
    window.sessionStorage.setItem(QUEUE_ORDER_KEY, JSON.stringify(clean));
  } catch {
    // No storage in this tab: the case page shows no Next / Previous.
  }
}

/** The snapshot of this tab, or null. */
export function readQueueOrder(): QueueOrder | null {
  try {
    const stored = window.sessionStorage.getItem(QUEUE_ORDER_KEY);
    return stored ? toQueueOrder(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
}

/** The snapshot with one more case marked as closed. */
export function withClosed(order: QueueOrder, caseId: string): QueueOrder {
  return order.closed.includes(caseId) ? order : { ...order, closed: [...order.closed, caseId] };
}

/** The snapshot with the ids of the next queue page appended (no duplicates, capped). */
export function withMore(order: QueueOrder, ids: readonly string[], total?: number): QueueOrder {
  const merged = [...new Set([...order.ids, ...ids])];
  const dropped = Math.max(0, merged.length - MAX_ORDER);
  return {
    ...order,
    ids: merged.slice(dropped),
    offset: order.offset + dropped,
    total: typeof total === "number" ? total : order.total,
  };
}

/** The cases before and after this one, skipping the closed ones and the case itself. */
export function neighbours(
  order: QueueOrder | null,
  caseId: string,
): { previous: string | null; next: string | null; position: number | null } {
  if (!order) {
    return { previous: null, next: null, position: null };
  }
  const at = order.ids.indexOf(caseId);
  if (at < 0) {
    return { previous: null, next: null, position: null };
  }
  const usable = (id: string) => id !== caseId && !order.closed.includes(id);
  const next = order.ids.slice(at + 1).find(usable) ?? null;
  const previous = order.ids.slice(0, at).reverse().find(usable) ?? null;
  return { previous, next, position: order.offset + at + 1 };
}

/** True when the queue has rows after the snapshot (the next page can be read). */
export function hasMoreRows(order: QueueOrder): boolean {
  return order.offset + order.ids.length < order.total;
}

/**
 * The queue search string for the page that follows the snapshot. Closed cases have left
 * the queue, so the next rows start that much earlier; ids already known are dropped by
 * {@link withMore}.
 */
export function nextPageSearch(order: QueueOrder, pageSize: number): string {
  const params = new URLSearchParams(order.search);
  const nextOffset = Math.max(0, order.offset + order.ids.length - order.closed.length);
  const page = Math.floor(nextOffset / pageSize) + 1;
  if (page <= 1) {
    params.delete("page");
  } else {
    params.set("page", String(page));
  }
  return params.toString();
}
